import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Card from '../UI/Card';
import Button from '../UI/Button';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { NotificationService } from '../../services/NotificationService';
import toast from 'react-hot-toast';

const { FiUpload, FiSave, FiLock, FiBell, FiMail, FiSend } = FiIcons;

const SettingsForm = ({ defaultValues, onSubmit }) => {
  const [logoPreview, setLogoPreview] = useState(defaultValues?.companyLogo || '');
  const [activeTab, setActiveTab] = useState('general');
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  // Initialize form with notification preferences
  const { register, handleSubmit, getValues, formState: { errors } } = useForm({
    defaultValues: {
      ...defaultValues,
      notificationPreferences: defaultValues?.notificationPreferences || {
        new_user_welcome: true,
        password_changed: true,
        task_assigned: true,
        task_status_updated: true,
        lead_assigned: true,
        contract_renewal: true,
        change_request_created: true,
        change_request_updated: true
      }
    }
  });

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const onFormSubmit = (data) => {
    const updatedSettings = {
      ...data,
      companyLogo: logoPreview,
      reminderDays: parseInt(data.reminderDays),
    };
    onSubmit(updatedSettings);
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    setSendingTest(true);
    try {
      // Get current form values to test with unsaved changes
      const currentFormValues = getValues();
      const settingsToTest = {
        ...defaultValues,
        ...currentFormValues,
        companyLogo: logoPreview // include local state
      };
      
      const result = await NotificationService.sendTestEmail(testEmail, settingsToTest);
      
      if (result && result.success) {
        if (result.simulated) {
          toast('Email simulated locally (Edge Function not reachable)', {
            icon: '⚠️',
            duration: 5000,
            style: {
              border: '1px solid #FCD34D',
              padding: '16px',
              color: '#92400E',
            },
          });
        } else {
          toast.success(`Test email successfully sent to ${testEmail}`);
        }
        setTestEmail('');
      } else {
        // If it failed completely (unexpected error)
        toast.error('Failed to initiate email test');
        console.error('Email failure details:', result?.error);
      }
    } catch (error) {
      toast.error('Failed to initiate email test');
      console.error(error);
    } finally {
      setSendingTest(false);
    }
  };

  const NotificationToggle = ({ label, name, description }) => (
    <div className="flex items-center justify-between py-4 border-b border-gray-100 last:border-0">
      <div>
        <h4 className="text-sm font-medium text-gray-900">{label}</h4>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      <div className="flex items-center">
        <label className="relative inline-flex items-center cursor-pointer">
          <input 
            type="checkbox" 
            {...register(`notificationPreferences.${name}`)} 
            className="sr-only peer" 
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
        </label>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          type="button"
          onClick={() => setActiveTab('general')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'general' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          General Information
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('notifications')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'notifications' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Notifications & Email
        </button>
      </div>

      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
        
        {activeTab === 'general' && (
          <>
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Company Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Logo
                  </label>
                  <div className="flex items-center space-x-4">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Company Logo" className="h-16 w-16 object-cover rounded-lg border border-gray-300" />
                    ) : (
                      <div className="h-16 w-16 bg-gray-100 rounded-lg border border-gray-300 flex items-center justify-center">
                        <SafeIcon icon={FiUpload} className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="hidden"
                        id="logo-upload"
                      />
                      <label
                        htmlFor="logo-upload"
                        className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <SafeIcon icon={FiUpload} className="h-4 w-4 mr-2" />
                        Upload Logo
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        Recommended: 200x200px, PNG or JPG
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      {...register('companyName', { required: 'Company name is required' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Enter company name"
                    />
                    {errors.companyName && (
                      <p className="mt-1 text-sm text-red-600">{errors.companyName.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Email
                    </label>
                    <input
                      type="email"
                      {...register('companyEmail')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Enter company email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Phone
                    </label>
                    <input
                      type="tel"
                      {...register('companyPhone')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Enter company phone"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Renewal Reminder Days
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      {...register('reminderDays', {
                        required: 'Reminder days is required',
                        min: { value: 1, message: 'Must be at least 1 day' },
                        max: { value: 365, message: 'Cannot exceed 365 days' }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                      placeholder="15"
                    />
                    {errors.reminderDays && (
                      <p className="mt-1 text-sm text-red-600">{errors.reminderDays.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Address
                  </label>
                  <textarea
                    {...register('companyAddress')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter company address"
                  />
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Data Management</h2>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="text-sm font-medium text-yellow-900 mb-2">Local Storage</h3>
                <p className="text-sm text-yellow-700">
                  All your CRM data is stored locally in your browser. To backup your data, you can export it manually. 
                  Clearing your browser data will remove all CRM information.
                </p>
              </div>
            </Card>
          </>
        )}

        {activeTab === 'notifications' && (
          <>
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <SafeIcon icon={FiMail} className="h-5 w-5 mr-2 text-gray-500" />
                Email Service Configuration
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    EmailIt API Key
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiLock} className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="password"
                      {...register('emailitApiKey')}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Enter your EmailIt API Key"
                      autoComplete="new-password"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Enter the API Key provided by your email service. This will be used as the password for SMTP authentication.
                  </p>
                </div>

                {/* Test Email Section */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Send Test Email
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="Enter recipient email"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-sm"
                    />
                    <Button 
                      type="button" 
                      onClick={handleSendTestEmail} 
                      disabled={sendingTest || !testEmail}
                      loading={sendingTest}
                      size="sm"
                    >
                      <SafeIcon icon={FiSend} className="h-4 w-4 mr-2" />
                      Send Test
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Verify your email configuration. If Edge Function is not deployed, a local simulation will run.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <SafeIcon icon={FiBell} className="h-5 w-5 mr-2 text-gray-500" />
                Notification Preferences
              </h2>
              <div className="space-y-1">
                <NotificationToggle 
                  label="New User Welcome" 
                  name="new_user_welcome" 
                  description="Send welcome email with credentials when a new user is created." 
                />
                <NotificationToggle 
                  label="Password Changed" 
                  name="password_changed" 
                  description="Notify user when their password has been changed." 
                />
                <NotificationToggle 
                  label="Task Assigned" 
                  name="task_assigned" 
                  description="Notify user when a new task is assigned to them." 
                />
                <NotificationToggle 
                  label="Task Status Update" 
                  name="task_status_updated" 
                  description="Notify task creator when the assignee updates the status." 
                />
                <NotificationToggle 
                  label="Lead Assigned" 
                  name="lead_assigned" 
                  description="Notify user when a new lead is assigned to them." 
                />
                <NotificationToggle 
                  label="Contract Renewal Reminder" 
                  name="contract_renewal" 
                  description="Allow sending manual renewal reminders to clients." 
                />
                <NotificationToggle 
                  label="Change Request Created" 
                  name="change_request_created" 
                  description="Notify admins when a new change request is submitted." 
                />
                <NotificationToggle 
                  label="Change Request Status Update" 
                  name="change_request_updated" 
                  description="Notify relevant parties when a change request status changes." 
                />
              </div>
            </Card>
          </>
        )}

        <div className="flex justify-end sticky bottom-0 bg-white/90 p-4 border-t border-gray-200">
          <Button type="submit" size="lg">
            <SafeIcon icon={FiSave} className="h-4 w-4 mr-2" />
            Save Settings
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SettingsForm;